import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { terminalIntegrationStore } from '@/integrations/terminal-integration.store';
import { useTerminalIntegrationState } from '@/integrations/useTerminalIntegrationState';
import { cn } from '@/lib/utils';

import type { ConfigurationContext } from './configuration.types';

/**
 * Two-state access banner for the on-device Terminal toolkit. Mirrors
 * `DevicePermissionBanner` so the page reads like its Calendar/Contacts
 * siblings, but the switch here is Littlebird's own opt-in rather than a
 * macOS permission, so there is no "blocked" state and no System Settings
 * escape hatch. Enabling needs an explicit acknowledgement first: this is the
 * one page that spells out what the agent may then do, and every "+" for
 * Terminal elsewhere in the app lands here rather than flipping the switch.
 */
export const TerminalConfiguration = observer(
  function TerminalConfiguration(_props: { context: ConfigurationContext }) {
    const { terminalEnabled, terminalLoaded } = useTerminalIntegrationState();
    const pending = !terminalLoaded || terminalIntegrationStore.pending;
    // Not persisted on purpose: turning Terminal back on later gets the same
    // beat of reading what it means.
    const [acknowledged, setAcknowledged] = useState(false);

    const setEnabled = async (enabled: boolean) => {
      try {
        await terminalIntegrationStore.setEnabled(enabled);
      } catch (error) {
        console.error('Failed to update Terminal access:', error);
        toast.error(
          enabled ? "Couldn't enable Terminal" : "Couldn't disable Terminal"
        );
      }
    };

    const Icon = terminalEnabled ? Icons.check : Icons.terminal;

    return (
      <YStack className="gap-4 p-6">
        <XStack
          className={cn(
            'items-center justify-between gap-6 rounded-lg border px-4 py-3',
            terminalEnabled
              ? 'border-green-300/40 bg-green-200/40 dark:border-green-900/40 dark:bg-green-950/50'
              : 'border-border bg-surface dark:border-border-strong'
          )}
        >
          <XStack className="min-w-0 items-start gap-3">
            <Icon
              className={cn(
                'mt-0.5 size-4 shrink-0',
                terminalEnabled
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-foreground-muted'
              )}
            />
            <YStack className="gap-0.5">
              <p className="text-sm font-medium text-foreground">
                {terminalEnabled ? 'Terminal is enabled' : 'Terminal is off'}
              </p>
              <p className="text-xs text-foreground-muted">
                {terminalEnabled
                  ? 'Littlebird can run shell commands on this computer as you, without asking first. Commands appear in the terminal panel.'
                  : 'When enabled, Littlebird can run any shell command on this computer as you, without asking first - including commands that change or delete files, install software, or reach the network. Commands appear in the terminal panel, and you can disable this at any time.'}
              </p>
              {terminalEnabled ? null : (
                <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs text-foreground">
                  <Checkbox
                    className="mt-0.5"
                    checked={acknowledged}
                    disabled={pending}
                    onCheckedChange={(checked) =>
                      setAcknowledged(checked === true)
                    }
                  />
                  <span>
                    I understand Littlebird will be able to run any command on
                    this computer as me.
                  </span>
                </label>
              )}
            </YStack>
          </XStack>
          {terminalEnabled ? (
            // Nothing to fix in the enabled state, so the off switch is a quiet
            // text action rather than a button competing for attention.
            <button
              type="button"
              disabled={pending}
              onClick={() => void setEnabled(false)}
              className="shrink-0 text-xs text-foreground-muted transition-colors hover:text-foreground disabled:opacity-50"
            >
              {terminalIntegrationStore.pending ? 'Disabling…' : 'Disable'}
            </button>
          ) : (
            <Button
              size="sm"
              className="shrink-0"
              disabled={pending || !acknowledged}
              onClick={() => void setEnabled(true)}
            >
              {terminalIntegrationStore.pending ? 'Enabling…' : 'Enable'}
            </Button>
          )}
        </XStack>
      </YStack>
    );
  }
);
